import React, { useMemo } from 'react'
import { connect } from 'react-redux'

import { ShortlistState } from '@store/actionSlices/shortlist'
import {
  ProjectIdentity,
  RootStateTypeExtra,
  ShortlistPropertyType,
  UnitShortlistInterface,
} from '@store/types'

import usePropertyCompareFirebaseTrigger from '@components/compare-property/utils'

import ShortlistHandler from '@utilities/shortlist-handler'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import {
  BathSvg,
  BedSvg,
  CarSvg,
  PowderSvg,
  StarSvg,
  StudySvg,
} from '@svg/react'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { slugify } from '@adUtilities/helper'
import { notifyError } from '@adUtilities/notifier'
import { PropertyPositionInCompareMode, PropertyType } from '@adUtilities/types'
import { Unit } from '@adUtilities/types/apartment'

interface ComponentPropsInterface {
  unit: Unit
  active: boolean
  shortlist: ShortlistState
  projectIdentity: ProjectIdentity
  comparePropertyProps: ReturnType<typeof useCompareProperty>
}

function UnitCard({
  unit,
  active = false,
  shortlist,
  projectIdentity,
  comparePropertyProps,
}: ComponentPropsInterface) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })
  const { disableSoldProperty, statusLabels, statusColors } =
    projectIdentity || {}

  const unitStatus = unit.metas.status

  const isSoldUnit = useMemo(
    () => disableSoldProperty && unitStatus !== statusLabels?.available,
    [unit, statusLabels]
  )

  const [removeShortlistProperty, setRemoveShortlistProperty] =
    React.useState(false)
  const property = React.useMemo<UnitShortlistInterface>(
    () => ({
      propertyId: unit.id,
      propertyName: unit.name,
      references: {
        blockId: unit.blockId,
        levelId: unit.metas.level,
      },
      type: ShortlistPropertyType.Unit,
    }),
    [unit]
  )

  const setShortlistErrorMessage = (message: string) => {
    notifyError(message)
  }

  const [isShortlisted, handleShortlistClick] =
    (property &&
      ShortlistHandler({
        property,
        shortlist,
        setShortlistErrorMessage,
        projectIdentity,
        toggleConfirmModal: () => setRemoveShortlistProperty(true),
        removeShortlistProperty,
        setRemoveShortlistProperty,
        available: unit.metas.status,
      })) ||
    []

  const {
    checkPropertyPositionInCompareMode,
    compareModeIsActive,
    checkPropertyComparability,
  } = comparePropertyProps

  const { triggerComparisonAdd, triggerComparisonRemove } =
    usePropertyCompareFirebaseTrigger({
      comparePropertyProps,
    })

  const isComparable = React.useMemo(
    () =>
      checkPropertyComparability({
        data: unit,
        propertyType: PropertyType.Unit,
      }),
    [checkPropertyComparability, unit]
  )
  const propertyPositionInCompareMode = React.useMemo(
    () => checkPropertyPositionInCompareMode(unit.id),
    [checkPropertyPositionInCompareMode, unit.id]
  )

  const handleComparisonClick = React.useCallback(() => {
    if (propertyPositionInCompareMode) {
      triggerComparisonRemove({
        index: propertyPositionInCompareMode.index,
        keepLast: true,
      })
      return
    }
    if (!isComparable) {
      return
    }
    triggerComparisonAdd({
      property: {
        propertyType: PropertyType.Unit,
        data: unit,
        references: {
          levelId: unit.metas.level,
        },
      },
    })
  }, [
    propertyPositionInCompareMode,
    triggerComparisonRemove,
    isComparable,
    triggerComparisonAdd,
    unit,
  ])

  const handleUnitClick = async () => {
    if (compareModeIsActive) {
      handleComparisonClick()
      return
    }
    await firebaseControlQuery.update({
      [`building.activeUnit`]: unit.name,
      [`building.activeLevel`]: unit.metas.level,
      [`propertyGallery.galleryControlV2.activeSlideIndex`]: 0,
    })
  }

  const statusColor = useMemo(
    () => statusColors[slugify(unitStatus || '')] || '#EF4444',
    [statusColors]
  )

  const className = React.useMemo<string>(
    () =>
      `mb-2.5 rounded-md ${
        !compareModeIsActive && active ? 'border-l-8 border-mainColour' : ''
      } bg-white ${isSoldUnit ? 'pointer-events-none !bg-[#c2c2c2]' : ''} ${
        propertyPositionInCompareMode?.position ===
        PropertyPositionInCompareMode.First
          ? 'border-l-8 border-mainColour'
          : ''
      } ${
        propertyPositionInCompareMode?.position ===
        PropertyPositionInCompareMode.Last
          ? 'border-r-8 border-mainColour'
          : ''
      } ${
        compareModeIsActive && !isComparable
          ? 'cursor-not-allowed'
          : 'cursor-pointer'
      }`,
    [
      active,
      compareModeIsActive,
      isSoldUnit,
      propertyPositionInCompareMode,
      isComparable,
    ]
  )

  return (
    <div role="none" className={className} onClick={handleUnitClick}>
      <div className="px-6 py-4">
        <div className="mb-2 flex justify-between gap-1">
          <div className="flex min-w-0 items-center">
            <div className="flex-1 truncate">
              <span className="font-bold" title={unit.name}>
                {unit.name}
              </span>
            </div>
            <span
              className="ml-4 h-2 w-2 rounded-full"
              style={{
                backgroundColor: statusColor,
              }}
            ></span>
            <span
              className="ml-1 text-xs font-semibold uppercase tracking-widest"
              style={{
                color: statusColor,
              }}
            >
              {unitStatus}
            </span>
          </div>
          <button
            type="button"
            title="Shortlist unit"
            onClick={(e) => {
              e.stopPropagation()
              handleShortlistClick()
            }}
          >
            <StarSvg
              className={`h-4 w-4 ${
                isShortlisted
                  ? 'stroke-amber-500 text-amber-500'
                  : 'stroke-zinc-400 text-zinc-400'
              }`}
            />
          </button>
        </div>

        <div className="flex gap-3 text-zinc-500">
          {Number(unit.metas?.configurations?.bed) > 0 && (
            <div className="inline-flex items-center gap-1 text-sm" title="Bed">
              <BedSvg className="h-4 w-4" />
              {unit.metas?.configurations?.bed}
            </div>
          )}
          {Number(unit.metas?.configurations?.study) > 0 && (
            <div
              className="inline-flex items-center gap-1 text-sm"
              title="Study"
            >
              <StudySvg className="h-4 w-4" />
              {unit.metas?.configurations?.study}
            </div>
          )}
          {Number(unit.metas?.configurations?.bath) > 0 && (
            <div
              className="inline-flex items-center gap-1 text-sm"
              title="Bath"
            >
              <BathSvg className="h-4 w-4" />
              {unit.metas?.configurations?.bath}
            </div>
          )}
          {Number(unit.metas?.configurations?.powderRoom) > 0 && (
            <div
              className="inline-flex items-center gap-1 text-sm"
              title="Powder"
            >
              <PowderSvg className="h-4 w-4" />
              {unit.metas?.configurations?.powderRoom}
            </div>
          )}
          {Number(unit.metas?.configurations?.car) > 0 && (
            <div className="inline-flex items-center gap-1 text-sm" title="Car">
              <CarSvg className="h-4 w-4" />
              {unit.metas?.configurations?.car}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
export default connect(
  ({ shortlist, projectIdentity }: RootStateTypeExtra) => ({
    shortlist,
    projectIdentity,
  })
)(UnitCard)
